import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Phone } from 'lucide-react';

interface PhoneInputProps {
  value?: string; // phone (digits or formatted); onChange returns digits only
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  label?: string;
  helpText?: string;
  error?: string;
  onBlur?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Req = styled.span`
  color: ${(p) => p.theme.error};
  margin-left: 4px;
`;

const InputWrapper = styled.div<{ $disabled?: boolean; $hasError?: boolean }>`
  display: flex;
  align-items: center;
  background: ${(p) => (p.$disabled ? p.theme.surfaceAlt : p.theme.surface)};
  border: 1px solid ${(p) => (p.$hasError ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  box-sizing: border-box;
  min-height: 42px;
  padding: 0 0.75rem;
  transition: all 0.2s;

  &:focus-within {
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }

  .icon {
    display: inline-flex;
    align-items: center;
    color: ${(p) => p.theme.textMuted};
    margin-right: 0.5rem;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 0.6rem 0;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  color: ${(p) => p.theme.textPrimary};
  outline: none;

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const HelpText = styled.small<{ $isError?: boolean }>`
  font-size: 0.75rem;
  color: ${(p) => (p.$isError ? p.theme.error : p.theme.textMuted)};
`;

/** Keep only digits, capped at 11 (BR: DDD + number). */
const onlyDigits = (text: string): string => {
  let d = (text || '').replace(/\D/g, '');
  // Drop the Brazilian country code (55) when a full 12/13-digit number carries it.
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  return d.slice(0, 11);
};

/** Incremental Brazilian phone mask: (11) 91234-5678 / (11) 1234-5678. */
export const maskPhone = (digits: string): string => {
  const d = onlyDigits(digits);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  // 11-digit mobile: separate the leading 9 → (XX) X XXXX-XXXX
  return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3, 7)}-${d.slice(7)}`;
};

/** Format a phone for display (pt-BR); returns the original if it has no digits. */
export const formatPhone = (phone?: string | null): string => maskPhone(phone || '') || (phone || '');

/**
 * Standard phone field (pt-BR): phone icon, incremental masking while typing.
 * `onChange` returns digits only (e.g. "11912345678").
 */
export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = '(11) 91234-5678',
  disabled,
  required,
  className,
  id,
  name,
  autoFocus,
  label,
  helpText,
  error,
  onBlur,
}) => {
  const [display, setDisplay] = useState<string>('');

  useEffect(() => {
    setDisplay(maskPhone(value || ''));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = onlyDigits(e.target.value);
    setDisplay(maskPhone(digits));
    onChange(digits);
  };

  return (
    <Container className={className}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <Req>*</Req>}
        </Label>
      )}
      <InputWrapper $disabled={disabled} $hasError={!!error}>
        <span className="icon" aria-hidden="true">
          <Phone size={16} />
        </span>
        <StyledInput
          type="tel"
          inputMode="tel"
          value={display}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          id={id}
          name={name}
          autoFocus={autoFocus}
        />
      </InputWrapper>
      {(error || helpText) && <HelpText $isError={!!error}>{error || helpText}</HelpText>}
    </Container>
  );
};

export default PhoneInput;
